<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header dashboard-header-row">
        <div>
          <h1 class="dashboard-title">Masterartikel</h1>
          <p class="dashboard-subtitle">Master-Artikel zur Reparatur freigeben.</p>
        </div>
        <div class="search-wrap">
          <input
            v-model="searchTerm"
            type="search"
            class="form-control"
            placeholder="Suchen …"
            aria-label="Masterartikel durchsuchen"
          />
          <!-- Standardmäßig aus: Ausgeblendetes stört im Alltag. Eingeschaltet
               kann man es hier über „Einblenden" wieder in die Liste holen. -->
          <div class="form-check show-hidden">
            <input
              id="show-hidden"
              v-model="showHidden"
              class="form-check-input"
              type="checkbox"
              :disabled="loading"
              @change="reload"
            />
            <label class="form-check-label" for="show-hidden">Ausgeblendete auch anzeigen</label>
          </div>
        </div>
      </div>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <div v-else-if="grouped.length === 0" class="empty-state">
        <template v-if="searchTerm">Keine Treffer für „{{ searchTerm }}".</template>
        <template v-else>Keine Masterartikel vorhanden.</template>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle masterarticles-table">
          <thead>
            <tr>
              <th scope="col">Bezeichnung</th>
              <th scope="col" style="width: 180px;">Letzte Reparatur</th>
              <th scope="col" style="width: 220px;">Für Reparatur freigegeben</th>
              <th scope="col" style="width: 220px;"></th>
            </tr>
          </thead>
          <tbody v-for="vendorGroup in grouped" :key="vendorGroup.vendor.id">
            <tr class="group-header group-vendor">
              <th colspan="4" scope="rowgroup">{{ vendorGroup.vendor.description }}</th>
            </tr>
            <template v-for="typeGroup in vendorGroup.types" :key="vendorGroup.vendor.id + '-' + typeGroup.type.id">
              <tr class="group-header group-type">
                <th colspan="4" scope="rowgroup">{{ typeGroup.type.description }}</th>
              </tr>
              <tr v-for="ma in typeGroup.devices" :key="ma.id" :class="{ 'row-hidden': ma.is_hidden }">
                <td>
                  {{ ma.description }}
                  <span v-if="ma.is_hidden" class="hidden-badge">Ausgeblendet</span>
                </td>
                <td class="text-muted">—</td>
                <td>
                  <div class="form-check form-switch m-0">
                    <input
                      :id="`avail-${ma.id}`"
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      :checked="ma.is_available_for_repair"
                      :disabled="busyIds.has(ma.id) || ma.is_hidden"
                      @change="onToggle(ma, ($event.target as HTMLInputElement).checked)"
                    />
                    <label class="form-check-label" :for="`avail-${ma.id}`">
                      {{ ma.is_available_for_repair ? 'Ja' : 'Nein' }}
                    </label>
                  </div>
                </td>
                <td class="text-end">
                  <router-link
                    class="btn btn-sm btn-outline-secondary"
                    :to="{ name: 'masterarticle', params: { id: ma.id } }"
                  >
                    Details
                  </router-link>
                  <button
                    v-if="ma.is_hidden"
                    type="button"
                    class="btn btn-sm btn-outline-primary ms-2"
                    :disabled="busyIds.has(ma.id)"
                    @click="onUnhide(ma)"
                  >
                    Einblenden
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-sm btn-outline-danger ms-2"
                    :disabled="busyIds.has(ma.id)"
                    @click="onHide(ma)"
                  >
                    Ausblenden
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="toggleError" class="error toggle-error">{{ toggleError }}</div>
    </main>

    <ConfirmModal
      ref="confirmModal"
      title="Artikel ausblenden"
      confirm-label="Ausblenden"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import TopNav from '../components/TopNav.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import {
  list,
  setAvailability,
  hide,
  unhide,
  ApiError,
  type MasterArticle,
  type VendorRef,
  type DeviceTypeRef,
} from '../services/masterarticles';

interface TypeGroup {
  type:    DeviceTypeRef;
  devices: MasterArticle[];
}
interface VendorGroup {
  vendor: VendorRef;
  types:  TypeGroup[];
}

const items        = ref<MasterArticle[]>([]);
const loading      = ref(true);
const loadError    = ref<string | null>(null);
const toggleError  = ref<string | null>(null);
const busyIds      = ref<Set<number>>(new Set());
const searchTerm   = ref('');
const showHidden   = ref(false);
const confirmModal = useTemplateRef<InstanceType<typeof ConfirmModal>>('confirmModal');

onMounted(reload);

async function reload(): Promise<void> {
  loading.value   = true;
  loadError.value = null;
  try {
    items.value = await list(showHidden.value);
  } catch (e) {
    loadError.value = errorMessage(e);
  } finally {
    loading.value = false;
  }
}

const filtered = computed<MasterArticle[]>(() => {
  const q = searchTerm.value.trim().toLowerCase();
  if (q === '') return items.value;
  return items.value.filter(ma =>
    ma.description.toLowerCase().includes(q)
    || ma.vendor.description.toLowerCase().includes(q)
    || ma.hs2019DeviceType.description.toLowerCase().includes(q),
  );
});

const grouped = computed<VendorGroup[]>(() => {
  // Vendor.id -> { vendor, types: Map<typeId, {type, devices[]}> }
  const byVendor = new Map<number, { vendor: VendorRef; types: Map<number, TypeGroup> }>();

  for (const ma of filtered.value) {
    const vId = ma.vendor.id;
    if (!byVendor.has(vId)) {
      byVendor.set(vId, { vendor: ma.vendor, types: new Map() });
    }
    const v = byVendor.get(vId)!;
    const tId = ma.hs2019DeviceType.id;
    if (!v.types.has(tId)) {
      v.types.set(tId, { type: ma.hs2019DeviceType, devices: [] });
    }
    v.types.get(tId)!.devices.push(ma);
  }

  const result: VendorGroup[] = [];
  const vendors = [...byVendor.values()].sort((a, b) =>
    a.vendor.description.localeCompare(b.vendor.description, 'de'),
  );
  for (const v of vendors) {
    const types = [...v.types.values()].sort((a, b) =>
      a.type.description.localeCompare(b.type.description, 'de'),
    );
    for (const t of types) {
      t.devices.sort((a, b) => a.description.localeCompare(b.description, 'de'));
    }
    result.push({ vendor: v.vendor, types });
  }
  return result;
});

async function onToggle(ma: MasterArticle, newValue: boolean): Promise<void> {
  if (busyIds.value.has(ma.id)) return;
  toggleError.value = null;

  // Optimistic UI
  const oldValue = ma.is_available_for_repair;
  ma.is_available_for_repair = newValue;
  busyIds.value.add(ma.id);

  try {
    await setAvailability(ma.id, newValue);
  } catch (e) {
    ma.is_available_for_repair = oldValue;
    toggleError.value = `Speichern fehlgeschlagen: ${errorMessage(e)}`;
  } finally {
    busyIds.value.delete(ma.id);
  }
}

async function onHide(ma: MasterArticle): Promise<void> {
  if (busyIds.value.has(ma.id)) return;
  toggleError.value = null;

  const confirmed = await confirmModal.value?.open(
    `„${ma.description}" wirklich ausblenden? Der Artikel verschwindet aus der Liste und ist nicht mehr für Reparatur freigegeben.`,
  );
  if (!confirmed) return;

  busyIds.value.add(ma.id);
  try {
    await hide(ma.id);
    // Mit Hakerl bleibt die Zeile stehen (als ausgeblendet), sonst verschwindet sie.
    if (showHidden.value) {
      ma.is_hidden               = true;
      ma.is_available_for_repair = false;
    } else {
      items.value = items.value.filter(item => item.id !== ma.id);
    }
  } catch (e) {
    toggleError.value = `Ausblenden fehlgeschlagen: ${errorMessage(e)}`;
  } finally {
    busyIds.value.delete(ma.id);
  }
}

/** Wieder einblenden. Die Freigabe bleibt auf „Nein" — die schaltet man danach selbst. */
async function onUnhide(ma: MasterArticle): Promise<void> {
  if (busyIds.value.has(ma.id)) return;
  toggleError.value = null;

  busyIds.value.add(ma.id);
  try {
    await unhide(ma.id);
    ma.is_hidden = false;
  } catch (e) {
    toggleError.value = `Einblenden fehlgeschlagen: ${errorMessage(e)}`;
  } finally {
    busyIds.value.delete(ma.id);
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error)    return e.message;
  return 'Unbekannter Fehler.';
}
</script>

<style scoped>
.search-wrap {
  min-width: 280px;
}
.show-hidden {
  margin-top: 6px;
}
.row-hidden td {
  opacity: 0.6;
}
.hidden-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.08);
  font-size: 12px;
  font-weight: 600;
}
.masterarticles-table th[colspan] {
  font-weight: 600;
}
.group-vendor th {
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  color: var(--accent-hover, #e09600);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 16px;
  font-weight: 700;
  padding-top: 12px;
}
.group-type th {
  background: rgba(0, 0, 0, 0.03);
  padding-left: 24px;
  font-size: 15px;
  font-weight: 600;
}
.toggle-error {
  margin-top: 16px;
}
</style>
